import React from "react";
import { Button } from "rbx";
import { uppercaseFirstLetter, getObjectIdTimestamp } from "../../../utils";
import formatDate from "../../../utils/formatDate";

const ellipsisCellStyle = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
  maxWidth: "100%",
};

const baseReviewColumns = [
  {
    header: "Status",
    id: "status",
    accessorKey: "status",
    size: 100,
    minSize: 90,
  },
  {
    header: "Title",
    id: "title",
    accessorKey: "title",
    size: 88,
    minSize: 72,
  },
  {
    header: "Year",
    id: "year",
    size: 56,
    minSize: 48,
    accessorFn: (row) => {
      const createdAt = getObjectIdTimestamp(row.id);
      return createdAt ? createdAt.getFullYear().toString() : row.year || "N/A";
    },
  },
  {
    header: "Subject",
    id: "subject",
    size: 108,
    minSize: 88,
    accessorFn: (row) =>
      `${uppercaseFirstLetter(row.user?.firstName)} ${uppercaseFirstLetter(
        row.user?.lastName
      )}`,
    cell: ({ getValue }) => (
      <span style={ellipsisCellStyle} title={getValue()}>
        {getValue()}
      </span>
    ),
  },
  {
    header: "Due Date",
    id: "dateToCompleteBy",
    size: 84,
    minSize: 72,
    accessorFn: (row) =>
      row?.dateToCompleteBy && formatDate(row?.dateToCompleteBy),
  },
  {
    header: "Overdue",
    id: "overdue",
    size: 68,
    minSize: 60,
    accessorFn: (row) =>
      row?.dateToCompleteBy &&
      new Date(row?.dateToCompleteBy) < new Date() &&
      !row?.dateCompleted
        ? "Yes"
        : "No",
  },
  {
    header: "Date Completed",
    id: "dateCompleted",
    size: 100,
    minSize: 88,
    accessorFn: (row) => row?.dateCompleted && formatDate(row?.dateCompleted),
  },
  {
    header: "Template Version Id",
    id: "templateVersionId",
    accessorKey: "templateVersionId",
    size: 120,
    minSize: 100,
    cell: ({ getValue }) => (
      <span style={ellipsisCellStyle} title={getValue()}>
        {getValue()}
      </span>
    ),
  },
];

export const getReviewColumns = ({ onUpdateManager }) => {
  const managerColumn = {
    header: "Manager",
    id: "manager",
    accessorKey: "manager",
    size: 260,
    minSize: 260,
    maxSize: 260,
    cell: ({ row }) => {
      const review = row.original;
      const isIncomplete = review.status === "INCOMPLETE";
      const managerName = review.manager || "";

      return (
        <div
          className="manager-column-cell"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.5rem",
            flexWrap: "nowrap",
            width: "100%",
            minWidth: 0,
          }}
        >
          <span
            className="manager-column-cell__name"
            style={{
              ...ellipsisCellStyle,
              flex: "1 1 auto",
              minWidth: 0,
            }}
            title={managerName}
          >
            {managerName}
          </span>
          <Button
            className="manager-column-cell__button"
            color="primary"
            disabled={!isIncomplete}
            size="small"
            style={{
              flexShrink: 0,
              marginBottom: 0,
              padding: "0.2rem 0.45rem",
              fontSize: "0.7rem",
              lineHeight: 1.2,
              userSelect: "none",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onUpdateManager(review);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseEnter={(e) => {
              e.currentTarget.closest("tr")?.setAttribute("data-suppress-hover", "true");
            }}
            onMouseLeave={(e) => {
              e.currentTarget.closest("tr")?.removeAttribute("data-suppress-hover");
            }}
          >
            Update Manager
          </Button>
        </div>
      );
    },
  };

  return [
    baseReviewColumns[0],
    baseReviewColumns[1],
    baseReviewColumns[2],
    managerColumn,
    ...baseReviewColumns.slice(3),
  ];
};

export const REVIEW_COLUMNS = getReviewColumns({ onUpdateManager: () => {} });

export const CONTRACTOR_COLUMNS = [
  {
    Header: "Contractor Name",
    id: "contractor_name",
    accessor: (row) =>
      `${uppercaseFirstLetter(row.firstName)}  ${uppercaseFirstLetter(
        row.lastName
      )} `,
  },
  {
    Header: "Task",
    id: "task",
    accessor: "task",
  },
  {
    Header: "Staffing Agency",
    id: "staffingAgency",
    accessor: "staffingAgency",
  },
];
